import React, { useState } from 'react';
import { XMarkIcon, FolderIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import './MoveToFolderDialog.css';

function MoveToFolderDialog({
  isOpen,
  folderTree,
  currentFolderId,
  selectedCount,
  onMove,
  onClose
}) {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onMove(selectedFolderId);
      onClose();
    } catch (error) {
      console.error('Move failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFolderOption = (folder, level = 0) => {
    const isSelected = selectedFolderId === folder.id;
    const isCurrent = currentFolderId === folder.id;

    return (
      <div key={folder.id}>
        <button
          type="button"
          className={`folder-option ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => setSelectedFolderId(folder.id)}
          disabled={isCurrent}
        >
          <span className="folder-icon" style={{ color: folder.color }}>
            {getFolderEmoji(folder.icon)}
          </span>
          <span className="folder-name">{folder.name}</span>
          {isCurrent && <span className="current-badge">Current</span>}
        </button>
        {folder.children && folder.children.map(child =>
          renderFolderOption(child, level + 1)
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="move-dialog-overlay" onClick={onClose}>
      <div
        className="move-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="move-dialog-header">
          <h2>Move {selectedCount} {selectedCount === 1 ? 'item' : 'items'}</h2>
          <button className="close-btn" onClick={onClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        <div className="folder-list">
          {/* Root / Unfiled option */}
          <button
            type="button"
            className={`folder-option ${selectedFolderId === null ? 'selected' : ''} ${currentFolderId === null ? 'current' : ''}`}
            onClick={() => setSelectedFolderId(null)}
            disabled={currentFolderId === null}
          >
            <span className="folder-icon">📋</span>
            <span className="folder-name">Unfiled (Root)</span>
            {currentFolderId === null && <span className="current-badge">Current</span>}
          </button>

          {/* Folder tree */}
          {folderTree.map(folder => renderFolderOption(folder))}

          {folderTree.length === 0 && (
            <div className="empty-message">
              No folders yet. Create a folder to organize your transcripts.
            </div>
          )}
        </div>

        <div className="dialog-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="move-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedFolderId === currentFolderId}
          >
            {isSubmitting ? 'Moving...' : 'Move Here'}
          </button>
        </div>
      </div>
    </div>
  );
}

function getFolderEmoji(icon) {
  const icons = {
    'folder': '📁',
    'briefcase': '💼',
    'star': '⭐',
    'heart': '❤️',
    'bookmark': '🔖',
    'archive': '📦',
    'music': '🎵',
    'video': '🎬',
    'meeting': '👥',
    'personal': '👤',
    'work': '💻',
    'notes': '📝'
  };
  return icons[icon] || icons['folder'];
}

export default MoveToFolderDialog;
